<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import type { AdminRecord } from '@/types/record'
import type { FormFieldDef } from '@/types/page'
import type { MasterConfig } from '@/data/masters'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { adapter } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { ApiError } from '@/utils/errors'
import { toPlain } from '@/utils/clone'
import { formatDateTime } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'
import AppDrawer from '@/components/ui/AppDrawer.vue'
import AppDialog from '@/components/ui/AppDialog.vue'
import ProField from '@/components/pro/ProField.vue'
import ProSelect from '@/components/pro/ProSelect.vue'

const props = defineProps<{
  open: boolean
  config: MasterConfig | null
}>()

const emit = defineEmits<{
  close: []
  changed: []
}>()

const auth = useAuthStore()
const ui = useUiStore()
const rows = ref<AdminRecord[]>([])
const loading = ref(false)
const error = ref('')
const keyword = ref('')
const formOpen = ref(false)
const formMode = ref<'create' | 'edit'>('create')
const editing = ref<AdminRecord | null>(null)
const saving = ref(false)
const formError = ref('')
const fieldErrors = reactive<Record<string, string>>({})
const formValues = reactive<Record<string, unknown>>({})
const mergeOpen = ref(false)
const mergeSource = ref<AdminRecord | null>(null)
const mergeTarget = ref('')
const mergeError = ref('')
const merging = ref(false)

const canManage = computed(() => props.config?.pageId === 'users' ? auth.has('users.tag') : props.config?.pageId === 'drugs' ? auth.has('drugs.category') : props.config?.pageId === 'membership-plans' ? auth.has('membership.plan') : canFromConfig())
const filtered = computed(() => rows.value.filter(row => {
  if (!keyword.value) return true
  const text = keyword.value.toLowerCase()
  return Object.values(row).some(value => String(value ?? '').toLowerCase().includes(text))
}))
const activeTargets = computed(() => rows.value.filter(row => row.status === 'active' && String(row.id) !== String(mergeSource.value?.id)))
const fields = computed<FormFieldDef[]>(() => (props.config?.fields ?? []).map(field => ({
  key: field.key,
  label: field.label,
  type: field.type,
  required: field.required,
  options: field.options,
  maxLength: field.maxLength,
  placeholder: field.placeholder,
  help: field.help,
  full: field.type === 'textarea',
})))
const usageFor = (row: AdminRecord): number => props.config ? adapter.masterUsage(props.config, row) : 0
const conflict = ref<{ name: string; usage: number } | null>(null)

function canFromConfig(): boolean {
  if (!props.config) return false
  const permissionMap: Record<string, string> = {
    links: 'links.manage',
    'content-faq': 'content.manage',
    devices: 'devices.manage',
    'health-reports': 'reports.template',
  }
  return auth.has(permissionMap[props.config.pageId] ?? 'settings.manage')
}

function load(): void {
  if (!props.open || !props.config) return
  loading.value = true
  error.value = ''
  try {
    rows.value = adapter.masterRows(props.config)
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : '主数据加载失败'
  } finally {
    loading.value = false
  }
}

function openCreate(): void {
  if (!props.config || !canManage.value) return
  formMode.value = 'create'
  editing.value = null
  formError.value = ''
  for (const key of Object.keys(fieldErrors)) delete fieldErrors[key]
  for (const key of Object.keys(formValues)) delete formValues[key]
  for (const field of fields.value) {
    formValues[field.key] = field.type === 'select' ? (field.options?.[0]?.value ?? '') : field.type === 'multiselect' ? [] : ''
  }
  formOpen.value = true
}

function openEdit(row: AdminRecord): void {
  if (!props.config || !canManage.value) return
  formMode.value = 'edit'
  editing.value = row
  formError.value = ''
  for (const key of Object.keys(fieldErrors)) delete fieldErrors[key]
  for (const key of Object.keys(formValues)) delete formValues[key]
  for (const field of fields.value) {
    formValues[field.key] = Array.isArray(row[field.key]) ? [...row[field.key] as string[]] : (row[field.key] ?? '')
  }
  formOpen.value = true
}

function validate(): boolean {
  for (const key of Object.keys(fieldErrors)) delete fieldErrors[key]
  for (const field of fields.value) {
    if (!field.required) continue
    const value = formValues[field.key]
    if (value === '' || value === undefined || value === null || (Array.isArray(value) && !value.length)) {
      fieldErrors[field.key] = `请填写${field.label}`
    }
  }
  return Object.keys(fieldErrors).length === 0
}

async function saveMaster(): Promise<void> {
  if (!props.config || saving.value || !validate()) return
  saving.value = true
  formError.value = ''
  try {
    await request(
      () => {
        if (formMode.value === 'create') return adapter.masterCreate(props.config!, toPlain(formValues), auth.profile?.name ?? '管理员')
        return adapter.masterUpdate(props.config!, String(editing.value?.id), toPlain(formValues), auth.profile?.name ?? '管理员')
      },
      { write: true, delay: 300, useSimulation: true },
    )
    formOpen.value = false
    load()
    emit('changed')
    ui.toast('success', formMode.value === 'create' ? '已新增' : '已保存', `${props.config.title}已同步到筛选、表单与关联页面`)
  } catch (caught) {
    if (caught instanceof ApiError && caught.fieldErrors) Object.assign(fieldErrors, caught.fieldErrors)
    formError.value = caught instanceof Error ? caught.message : '保存失败，请重试'
    ui.toast('error', '保存失败', formError.value)
  } finally {
    saving.value = false
  }
}

async function toggleStatus(row: AdminRecord): Promise<void> {
  if (!props.config || !canManage.value) return
  if (row.status === 'active') {
    const usage = usageFor(row)
    if (usage > 0) {
      conflict.value = { name: String(row[props.config.nameKey]), usage }
      mergeSource.value = row
      mergeTarget.value = String(activeTargets.value[0]?.[props.config.nameKey] ?? '')
      mergeError.value = ''
      mergeOpen.value = true
      return
    }
    const allowed = await ui.confirm({
      title: `停用${props.config.title}`,
      message: `确认停用「${String(row[props.config.nameKey])}」？停用后不再出现在下拉选项与新建表单中。`,
      confirmText: '确认停用',
      tone: 'danger',
    })
    if (!allowed) return
    try {
      await request(
        () => adapter.masterUpdate(props.config!, String(row.id), { ...row, status: 'disabled' }, auth.profile?.name ?? '管理员'),
        { write: true, delay: 240, useSimulation: true },
      )
      load()
      emit('changed')
      ui.toast('success', '已停用', `${String(row[props.config.nameKey])}已从可用选项中移除`)
    } catch (caught) {
      error.value = caught instanceof Error ? caught.message : '停用失败'
      ui.toast('error', '停用失败', error.value)
    }
    return
  }
  try {
    await request(
      () => adapter.masterUpdate(props.config!, String(row.id), { ...row, status: 'active' }, auth.profile?.name ?? '管理员'),
      { write: true, delay: 220, useSimulation: true },
    )
    load()
    emit('changed')
    ui.toast('success', '已启用', `${String(row[props.config.nameKey])}已可用于筛选和表单`)
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : '启用失败'
    ui.toast('error', '启用失败', error.value)
  }
}

function openMerge(row: AdminRecord): void {
  if (!props.config) return
  conflict.value = { name: String(row[props.config.nameKey]), usage: usageFor(row) }
  mergeSource.value = row
  mergeTarget.value = String(activeTargets.value[0]?.[props.config.nameKey] ?? '')
  mergeError.value = ''
  mergeOpen.value = true
}

async function confirmMerge(): Promise<void> {
  if (!props.config || !mergeSource.value || !mergeTarget.value || merging.value) {
    mergeError.value = '请选择不同的目标主数据'
    return
  }
  merging.value = true
  mergeError.value = ''
  try {
    const result = await request(
      () => adapter.masterMerge(props.config!, String(mergeSource.value?.id), mergeTarget.value, auth.profile?.name ?? '管理员'),
      { write: true, delay: 360, useSimulation: true },
    )
    mergeOpen.value = false
    conflict.value = null
    load()
    emit('changed')
    ui.toast('success', '引用迁移完成', result.message)
  } catch (caught) {
    mergeError.value = caught instanceof Error ? caught.message : '迁移失败'
  } finally {
    merging.value = false
  }
}

async function remove(row: AdminRecord): Promise<void> {
  if (!props.config || !canManage.value) return
  const usage = usageFor(row)
  if (usage > 0) {
    openMerge(row)
    return
  }
  const allowed = await ui.confirm({
    title: `删除${props.config.title}`,
    message: `确认删除「${String(row[props.config.nameKey])}」？该操作只修改当前原型内存，并写入操作日志。`,
    confirmText: '删除',
    tone: 'danger',
  })
  if (!allowed) return
  try {
    await request(
      () => adapter.masterRemove(props.config!, String(row.id), auth.profile?.name ?? '管理员'),
      { write: true, delay: 260, useSimulation: true },
    )
    load()
    emit('changed')
    ui.toast('success', '已删除', `${String(row[props.config.nameKey])}已从当前原型内存移除`)
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : '删除失败'
  }
}

watch(() => [props.open, props.config?.key], () => load(), { immediate: true })
</script>

<template>
  <AppDrawer :open="open" :title="config?.title ?? '主数据维护'" :subtitle="config?.description" width="720px" @close="emit('close')">
    <div v-if="config" class="master-panel">
      <div class="page-toolbar">
        <el-input v-model="keyword" placeholder="搜索名称、编码或说明" clearable style="max-width: 240px" aria-label="搜索主数据" />
        <span class="tag tag--neutral">{{ filtered.length }} 条</span>
        <span class="page-toolbar__spacer" />
        <button type="button" class="btn btn--primary btn--sm" :disabled="!canManage" @click="openCreate"><AppIcon name="plus" :size="13" />{{ config.createLabel }}</button>
      </div>

      <div v-if="loading" class="app-loading" style="min-height: 220px"><div class="app-loading__card"><span class="app-loading__ring" /><span>正在加载主数据…</span></div></div>
      <div v-else-if="error" class="empty-state" style="min-height: 220px" role="alert">
        <h3 class="empty-state__title">加载失败</h3><p class="empty-state__text">{{ error }}</p>
        <button type="button" class="btn btn--primary" @click="load">重试</button>
      </div>
      <div v-else-if="!filtered.length" class="empty-state" style="min-height: 220px">
        <h3 class="empty-state__title">还没有{{ config.title }}</h3>
        <p class="empty-state__text">新增后会立即出现在筛选、表单与关联页面。</p>
        <button v-if="canManage" type="button" class="btn btn--primary" @click="openCreate">{{ config.createLabel }}</button>
      </div>
      <div v-else class="table-scroll">
        <table class="pro-table">
          <thead><tr><th>名称</th><th>状态</th><th>{{ config.usageLabel }}</th><th>更新时间</th><th class="is-actions">操作</th></tr></thead>
          <tbody>
            <tr v-for="row in filtered" :key="String(row.id)">
              <td>
                <div class="cell-main__title">{{ row[config.nameKey] }}</div>
                <div class="cell-main__sub">{{ row.model_code || row.description || row.report_type || '—' }}</div>
              </td>
              <td><span class="tag" :class="row.status === 'active' ? 'tag--success' : 'tag--neutral'">{{ row.status === 'active' ? '启用' : '停用' }}</span></td>
              <td><span class="tag" :class="usageFor(row) ? 'tag--warning' : 'tag--neutral'">{{ usageFor(row) }} 处</span></td>
              <td>{{ formatDateTime(row.updated_at) }}</td>
              <td class="is-actions">
                <div class="row-actions">
                  <button type="button" class="row-action" :disabled="!canManage" @click="openEdit(row)">编辑</button>
                  <span class="row-action-sep" />
                  <button type="button" class="row-action" :disabled="!canManage" @click="toggleStatus(row)">{{ row.status === 'active' ? '停用' : '启用' }}</button>
                  <span class="row-action-sep" />
                  <button v-if="usageFor(row)" type="button" class="row-action" :disabled="!canManage" @click="openMerge(row)">迁移引用</button>
                  <button v-else type="button" class="row-action row-action--danger" :disabled="!canManage" @click="remove(row)">删除</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="conflict && config" class="info-banner info-banner--warn" style="margin-top: 12px" role="alert">
        <AppIcon name="warning" :size="14" />
        <span>「{{ conflict.name }}」仍被 {{ conflict.usage }} 处引用。按一致性规则不能直接停用或删除，请先迁移引用到其他{{ config.title }}。</span>
        <button type="button" class="text-button" @click="openMerge(rows.find(row => String(row[config?.nameKey ?? 'name']) === conflict?.name) ?? rows[0]!)">立即迁移</button>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn" @click="emit('close')">关闭</button>
    </template>
  </AppDrawer>

  <AppDialog v-model="formOpen" :title="formMode === 'create' ? (config?.createLabel ?? '新增主数据') : `编辑${config?.title ?? ''}`" width="520px">
    <div v-if="formError" class="info-banner info-banner--danger" style="margin-bottom: 10px" role="alert">{{ formError }}</div>
    <div class="settings-form-grid">
      <ProField
        v-for="field in fields"
        :key="field.key"
        v-model="formValues[field.key]"
        :field="field"
        :options="field.options"
        :error="fieldErrors[field.key]"
        :disabled="saving"
      />
    </div>
    <template #footer>
      <button type="button" class="btn" :disabled="saving" @click="formOpen = false">取消</button>
      <button type="button" class="btn btn--primary" :disabled="saving" @click="saveMaster">{{ saving ? '保存中…' : '保存' }}</button>
    </template>
  </AppDialog>

  <AppDialog v-model="mergeOpen" title="迁移引用" subtitle="完成迁移后源数据才能停用" width="480px">
    <div v-if="mergeError" class="info-banner info-banner--danger" style="margin-bottom: 10px" role="alert">{{ mergeError }}</div>
    <p class="muted">将「{{ mergeSource?.[config?.nameKey ?? 'name'] }}」的 {{ conflict?.usage ?? 0 }} 处引用迁移到：</p>
    <ProSelect
      v-model="mergeTarget"
      :options="activeTargets.map(row => ({ value: String(row[config?.nameKey ?? 'name']), label: String(row[config?.nameKey ?? 'name']) }))"
      placeholder="选择目标"
      :filterable="true"
      aria-label="迁移目标"
    />
    <div class="info-banner info-banner--brand" style="margin-top: 10px">迁移只修改当前原型内存中的引用字段，并记录操作日志；源数据随后自动停用。</div>
    <template #footer>
      <button type="button" class="btn" :disabled="merging" @click="mergeOpen = false">取消</button>
      <button type="button" class="btn btn--primary" :disabled="merging || !mergeTarget" @click="confirmMerge">{{ merging ? '迁移中…' : '确认迁移' }}</button>
    </template>
  </AppDialog>
</template>

<style scoped>
.master-panel { min-width: 0; }
</style>
